﻿import type { PersonAdditionalData, PolicyHolderAdditionalData, SmartTravelInsuredPerson, WizardPropertyAdditionalData } from '../dtos'

// Chytré cestovko (GČP, pojištění majetku) - sdílená logika formuláře pojištěných osob

export const defaultSmartTravelInsuredPerson: SmartTravelInsuredPerson = {
    firstName: '',
    lastName: '',
    birthnumber: '',
    birthdate: null,
    isForeigner: false,
    nationalityFK: null,
    isPolicyHolder: false,
    isInsuredPerson: false
}

// pojištěná osoba odlišná od pojistníka - pokud je shodná, server posílá jen insuredPersonSameAs (a insuredPerson může být
// v datech formuláře přítomen jako prázdný inicializační objekt)
export const getSeparateInsuredPerson = (policyHolder?: PolicyHolderAdditionalData | null): PersonAdditionalData | null => {
    if (!policyHolder || policyHolder.insuredPersonSameAs != null) return null

    const insuredPerson = policyHolder.insuredPerson
    if (!insuredPerson?.id || insuredPerson.id == policyHolder.person?.id) return null

    return insuredPerson as PersonAdditionalData
}

// založení pojištěné osoby chytrého cestovka z pojistníka / pojištěného z K1
export const createSmartTravelPersonFromK1 = (
    source: PersonAdditionalData | null | undefined,
    flags: { isPolicyHolder: boolean, isInsuredPerson: boolean }
): SmartTravelInsuredPerson => ({
    ...defaultSmartTravelInsuredPerson,
    ...flags,
    firstName: source?.name ?? '',
    lastName: source?.surname ?? '',
    birthnumber: source?.birthNumber ?? '',
    birthdate: source?.birthday ?? null,
    isForeigner: source ? !source.birthNumber : false,
    nationalityFK: source?.nationalityId ?? null
})

// první osobou je pojištěný z K1, případně pojistník (pokud je pojištěná osoba shodná s pojistníkem).
// Pokud je pojištěný shodný s pojistníkem, musí osoba nést příznak isPolicyHolder (builder GČP s ní pak správně pracuje)
const buildPrefilledInsuredPerson = (data: WizardPropertyAdditionalData): SmartTravelInsuredPerson => {
    const policyHolderPerson = data.policyHolder?.person as PersonAdditionalData | null
    const insuredPerson = getSeparateInsuredPerson(data.policyHolder)
    const hasSeparateInsuredPerson = !!insuredPerson

    return createSmartTravelPersonFromK1(
        hasSeparateInsuredPerson ? insuredPerson : policyHolderPerson,
        { isPolicyHolder: !hasSeparateInsuredPerson, isInsuredPerson: hasSeparateInsuredPerson }
    )
}

// předpřipravení řádků podle počtu osob zvoleného na kroku s nabídkami (rodina = 3 řádky), doplnění / oříznutí u již uložených dat.
// Volá se při přípravě defaultValues formuláře - nesmí běžet až v useEffectu, protože reset formuláře by přidané řádky přepsal
export const prepareSmartTravelInsuredPersons = (data?: WizardPropertyAdditionalData) => {
    const smartTravel = data?.property?.additionalDataGcp?.smartTravel
    if (!smartTravel) return

    const isFamily = smartTravel.insuredCount == 3
    const maxPersons = isFamily ? 7 : (smartTravel.insuredCount ?? 1)
    const persons = smartTravel.insuredPersons ?? []

    if (persons.length == 0) {
        const initialCount = isFamily ? 3 : maxPersons

        smartTravel.insuredPersons = [
            buildPrefilledInsuredPerson(data!),
            ...Array.from({ length: initialCount - 1 }, () => ({ ...defaultSmartTravelInsuredPerson }))
        ]
        return
    }

    if (isFamily) {
        // rodina vyžaduje minimálně 3 osoby
        if (persons.length < 3)
            smartTravel.insuredPersons = [
                ...persons,
                ...Array.from({ length: 3 - persons.length }, () => ({ ...defaultSmartTravelInsuredPerson }))
            ]

        return
    }

    if (persons.length > maxPersons)
        smartTravel.insuredPersons = persons.slice(0, maxPersons)

    if (persons.length < maxPersons)
        smartTravel.insuredPersons = [
            ...persons,
            ...Array.from({ length: maxPersons - persons.length }, () => ({ ...defaultSmartTravelInsuredPerson }))
        ]
}
